import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Modal } from "../atoms";
import ProteinResults from "./ProteinResults";
import { setProteinViewerState } from "../../state/actions/genericActions";
import {
  getGenome,
  getLoading,
  getVarsomeHost,
  getQueryType,
  getVariantSpec,
  getUserQuery,
} from "../../state/selectors/general.selectors";
import {
  getProteinViewerData,
  getTranscripts,
} from "./selectors/ProteinViewer.selectors";
import {
  proteinViewerDataType,
  refGenomeType,
} from "../cards/sharedCards/ProteinViewerCard/types/types";

const ProteinResultsContainer = ({
  refGenome,
  loadingState,
  varsomeHost,
  stateQueryType,
  variantSpec,
  proteinViewerData,
  setProteinViewerStateAction,
  layout,
}) => {
  if (loadingState) return null;
  const { error, results, finalTranscripts, isOpenThroughCards } =
    proteinViewerData;

  if (!results || !results.length || error) return null;

  const queryType = useMemo(
    () => layout || stateQueryType,
    [layout, stateQueryType]
  );
  const [mostValuableUrl, setMostValuableUrl] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentProtein, setCurrentProtein] = useState(null);
  const [currentVariantProtein, setCurrentVariantProtein] = useState(null);
  const [avaibleFileOptions, setAvaibleFileOptions] = useState(null);
  const [, setCurrentVariantChecked] = useState(false);
  const [displayCurrentVariantCheckbox, setDisplayCurrentVariantCheckbox] =
    useState(true);

  const getCurrentVariantChecked = (selected) => {
    setCurrentVariantChecked(selected);
  };

  const getCurrentVariantProtein = (result, shouldSetCurrentVariant = true) => {
    const cvp = {
      ...result,
      likely_benign: result.likely_benign?.filter(
        (item) => item.spec === variantSpec
      ),
      likely_pathogenic: result.likely_pathogenic?.filter(
        (item) => item.spec === variantSpec
      ),
      uncertain_significance: result.uncertain_significance?.filter(
        (item) => item.spec === variantSpec
      ),
      benign: result.benign?.filter((item) => item.spec === variantSpec),
      pathogenic: result.pathogenic?.filter(
        (item) => item.spec === variantSpec
      ),
    };
    if (shouldSetCurrentVariant) setCurrentVariantProtein(cvp);
    if (
      cvp.likely_benign.length === 0 &&
      cvp.likely_pathogenic.length === 0 &&
      cvp.uncertain_significance.length === 0 &&
      cvp.benign.length === 0 &&
      cvp.pathogenic.length === 0
    ) {
      if (shouldSetCurrentVariant) setDisplayCurrentVariantCheckbox(false);
      return null;
    }
    if (shouldSetCurrentVariant) setDisplayCurrentVariantCheckbox(true);
    return cvp;
  };

  const getBestAvaibleVariantInformationFromUrl = () => {
    if (!results || results.length === 0) return null;

    const filteredResults = results
      .map((result) => getCurrentVariantProtein(result, false))
      .filter((res) => res !== null);
    if (filteredResults.length === 0) return null;
    return filteredResults?.length > 1
      ? filteredResults
          .map((result) => {
            return { range: Math.abs(result.from - result.to), ...result };
          })
          .sort((a, b) => b.range - a.range)
      : filteredResults;
  };

  const getResultFromUrl = (url) => {
    setCurrentProtein(
      results.filter((proteinInfo) => proteinInfo.pdb_file === url.value)[0]
    );
    getCurrentVariantProtein(
      results.filter((proteinInfo) => proteinInfo.pdb_file === url.value)[0]
    );
  };

  useEffect(() => {
    if (queryType === "gene" || !results) return;
    const resultUrl = getBestAvaibleVariantInformationFromUrl();
    if (!resultUrl) {
      setMostValuableUrl(null);
      return;
    }
    setMostValuableUrl(getBestAvaibleVariantInformationFromUrl()[0]?.pdb_file);
  }, [results]);

  useEffect(() => {
    if (!results || results.length === 0) return;
    const resultUrl = getBestAvaibleVariantInformationFromUrl();
    setCurrentProtein(
      resultUrl
        ? results.filter(
            (proteinInfo) => proteinInfo.pdb_file === resultUrl[0]?.pdb_file
          )[0]
        : results[0]
    );
    setAvaibleFileOptions(
      results.map((proteinInfo) => ({
        to: proteinInfo.to,
        from: proteinInfo.from,
        url: proteinInfo.pdb_file,
        template: proteinInfo.template,
        pathoVariants: new Set(
          proteinInfo.pathogenic.map((value) => value.codon)
        ).size,
      }))
    );
    getCurrentVariantProtein(
      resultUrl
        ? results.filter(
            (proteinInfo) => proteinInfo.pdb_file === resultUrl[0]?.pdb_file
          )[0]
        : results[0]
    );
  }, [results]);

  return (
    <Modal
      isOpen={isOpenThroughCards}
      customModalStyle={
        isFullScreen
          ? { height: "100%", width: "100%", minHeight: "100%" }
          : null
      }
      onRequestClose={() => setProteinViewerStateAction(false)}
      ContentRender={() => {
        return (
          <ProteinResults
            getCurrentVariantChecked={getCurrentVariantChecked}
            currentProtein={currentProtein}
            transcriptInformation={finalTranscripts}
            varsomeHost={varsomeHost}
            refGenomeName={refGenome?.name}
            onProteinFileChange={getResultFromUrl}
            avaibleFileOptions={avaibleFileOptions}
            variantSpec={variantSpec}
            displayCurrentVariantCheckbox={
              queryType !== "gene" ? displayCurrentVariantCheckbox : false
            }
            queryType={queryType}
            mostValuableUrl={mostValuableUrl}
            currentVariantProtein={currentVariantProtein}
            setIsFullScreen={setIsFullScreen}
            isFullScreen={isFullScreen}
          />
        );
      }}
    />
  );
};

ProteinResultsContainer.propTypes = {
  refGenome: refGenomeType,
  proteinViewerData: proteinViewerDataType,
  setProteinViewerStateAction: PropTypes.func.isRequired,
  loadingState: PropTypes.bool,
  varsomeHost: PropTypes.string,
  stateQueryType: PropTypes.string,
  variantSpec: PropTypes.string,
  layout: PropTypes.string,
};

ProteinResultsContainer.defaultProps = {
  refGenome: null,
  proteinViewerData: PropTypes.shape({
    results: null,
    error: false,
    finalTranscripts: [],
  }),
  loadingState: false,
  varsomeHost: "",
  stateQueryType: null,
  variantSpec: "",
  layout: null,
};

const mapStateToProps = (state) => ({
  refGenome: getGenome(state),
  transcripts: getTranscripts(state),
  loadingState: getLoading(state),
  varsomeHost: getVarsomeHost(state),
  stateQueryType: getQueryType(state),
  variantSpec: getVariantSpec(state),
  proteinViewerData: getProteinViewerData(state),
  query: getUserQuery(state),
});

const mapDispatchToProps = (dispatch) => ({
  setProteinViewerStateAction: (params) =>
    dispatch(setProteinViewerState(params)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProteinResultsContainer);
